import { EyeOutlined } from "@ant-design/icons";
import { Button, Space, type TableColumnsType } from "antd";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import PermissionCheck from "~Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UserRoles } from "~enums/userRoles";
import type { LabelValue } from "~types/labelValue";
import { PermissionProducts } from "../../enums/permissionProducts";
import { PermissionScopeTypes } from "../../enums/permissionScopeTypes";
import type { UserPermissionTransformed } from "../../types";
import { PermissionNameList, PermissionScopeValue } from "../components";

const useGetUserPermissionsTableColumns = (
  product: PermissionProducts | undefined,
  deletePermission: (role: string, permissionId: number) => void,
  onViewPermissionClick: (permissionData: UserPermissionTransformed) => void,
): TableColumnsType<UserPermissionTransformed> => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const columns: TableColumnsType<UserPermissionTransformed> = [
    {
      title: formatMessage({ id: "permissions.user.role" }),
      dataIndex: "userRole",
      width: 150,
      render: (value: UserRoles) => formatMessage({ id: `permissions.userRole.${value}` }),
    },
  ];

  if (product === PermissionProducts.TeamManager) {
    columns.push(
      {
        title: formatMessage({ id: "entitygroups" }),
        dataIndex: "entityGroups",
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) => (
          <PermissionScopeValue value={value} scopeType={permission.scope} />
        ),
      },
      {
        title: formatMessage({ id: "entities" }),
        dataIndex: "entities",
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) => (
          <PermissionScopeValue value={value} scopeType={permission.scope} />
        ),
      },
    );
  } else {
    columns.push(
      {
        title: formatMessage({ id: "leagues" }),
        dataIndex: "leagues",
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) => (
          <PermissionScopeValue value={value} scopeType={permission.scope} />
        ),
        ellipsis: { showTitle: false },
      },
      {
        title: formatMessage({ id: "competitions" }),
        dataIndex: "competitions",
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) =>
          permission.scope === PermissionScopeTypes.League ? (
            formatMessage({ id: "all" })
          ) : (
            <PermissionScopeValue value={value} scopeType={permission.scope} />
          ),
        ellipsis: { showTitle: false },
      },
      {
        title: formatMessage({ id: "seasons" }),
        dataIndex: "seasons",
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) =>
          permission.scope === PermissionScopeTypes.League || permission.scope === PermissionScopeTypes.Competition ? (
            formatMessage({ id: "all" })
          ) : (
            <PermissionScopeValue value={value} scopeType={permission.scope} />
          ),
        ellipsis: { showTitle: false },
      },
      {
        title: formatMessage({ id: "season.statuses" }),
        dataIndex: "seasonStatuses",
        render: (value: string[] | null) => <PermissionNameList value={value} />,
      },
      {
        title: formatMessage({ id: "fixture.statuses" }),
        dataIndex: "fixtureStatuses",
        render: (value: string[] | null) => <PermissionNameList value={value} />,
      },
    );
  }

  columns.push({
    align: "center",
    key: "actions",
    width: 100,
    render: (_, permission: UserPermissionTransformed) => (
      <Space>
        <Button onClick={() => onViewPermissionClick(permission)} type="text" size="small" icon={<EyeOutlined />} />
        <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.UPDATE]}>
          <Button
            disabled={permission.isGroupPermission || permission.disabled}
            onClick={() => navigate(`permission/${permission.permissionId}`)}
            type="text"
            size="small"
            icon={<i className="fas fa-pen-to-square" />}
          />
        </PermissionCheck>
        <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.DELETE]}>
          <Button
            disabled={permission.isGroupPermission || permission.disabled}
            onClick={() => deletePermission(permission.userRole, permission.permissionId)}
            type="text"
            size="small"
            icon={<i className="fas fa-trash-can mr-2" />}
          />
        </PermissionCheck>
      </Space>
    ),
  });

  return columns;
};

export default useGetUserPermissionsTableColumns;
